import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui';
import { MusicIcon, VisualArtIcon, WritingIcon, VideoIcon } from '../icons';
import { CreateSubProjectRequest, WorkflowTemplateDto, workflowTemplatesApi, isSystemTemplate } from '../../services';
import './CreateSubProjectModal.css';

interface CreateSubProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateSubProjectRequest) => Promise<void>;
  parentProjectTitle: string;
  defaultProjectType?: string;
}

const WORK_TYPES = [
  { value: 'Music', labelKey: 'works.types.music', icon: <MusicIcon size={20} /> },
  { value: 'Art', labelKey: 'works.types.art', icon: <VisualArtIcon size={20} /> },
  { value: 'Writing', labelKey: 'works.types.writing', icon: <WritingIcon size={20} /> },
  { value: 'Video', labelKey: 'works.types.video', icon: <VideoIcon size={20} /> },
];

export const CreateSubProjectModal: React.FC<CreateSubProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  parentProjectTitle,
  defaultProjectType = 'Music',
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState(defaultProjectType);
  const [selectedStage, setSelectedStage] = useState('');
  const [template, setTemplate] = useState<WorkflowTemplateDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch the system template for the selected project type
  // Sub-projects are always Pieces, so we explicitly request Piece category
  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const templates = await workflowTemplatesApi.getAll(projectType, 'Piece');
        const systemTemplate = templates.find((t) => isSystemTemplate(t));
        if (systemTemplate) {
          setTemplate(systemTemplate);
          if (systemTemplate.stages.length > 0) {
            setSelectedStage(systemTemplate.stages[0]);
          }
        } else {
          setTemplate(null);
        }
      } catch (err) {
        console.error('Failed to fetch template:', err);
        setTemplate(null);
      }
    };

    if (isOpen) {
      fetchTemplate();
    }
  }, [projectType, isOpen]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setTitle('');
      setDescription('');
      setProjectType(defaultProjectType);
      setSelectedStage('');
      setError(null);
    }
  }, [isOpen, defaultProjectType]);

  const handleProjectTypeChange = (type: string) => {
    setProjectType(type);
    setSelectedStage('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError(t('works.subProjects.errors.titleRequired'));
      return;
    }

    if (!selectedStage) {
      setError(t('works.form.selectStage'));
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        title: title.trim(),
        description: description.trim() || undefined,
        projectType,
        initialStage: selectedStage,
        workflowTemplateId: template?.id,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('works.subProjects.errors.createFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('works.subProjects.createSubProject')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="create-sub-project-form">
        <p className="create-sub-project-form__parent-info">
          {t('works.subProjects.parentLabel')}: <strong>{parentProjectTitle}</strong>
        </p>

        {error && <div className="create-sub-project-form__error">{error}</div>}

        <div className="create-sub-project-form__field">
          <label htmlFor="subproject-title">{t('works.form.title')} *</label>
          <input
            id="subproject-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('works.subProjects.titlePlaceholder')}
            maxLength={200}
            required
          />
        </div>

        <div className="create-sub-project-form__field">
          <label htmlFor="subproject-description">{t('works.form.description')}</label>
          <textarea
            id="subproject-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('works.subProjects.descriptionPlaceholder')}
            rows={2}
          />
        </div>

        <div className="create-sub-project-form__field">
          <label>{t('works.form.projectType')}</label>
          <div className="create-sub-project-form__type-grid">
            {WORK_TYPES.map((type) => (
              <button
                key={type.value}
                type="button"
                className={`create-sub-project-form__type-btn ${projectType === type.value ? 'create-sub-project-form__type-btn--selected' : ''}`}
                onClick={() => handleProjectTypeChange(type.value)}
              >
                <span className="create-sub-project-form__type-icon">{type.icon}</span>
                <span className="create-sub-project-form__type-label">{t(type.labelKey)}</span>
                {projectType === type.value && (
                  <Check size={14} className="create-sub-project-form__type-check" />
                )}
              </button>
            ))}
          </div>
        </div>

        {template && (
          <div className="create-sub-project-form__field">
            <label>{t('works.form.initialStage')}</label>
            <div className="create-sub-project-form__stage-list">
              {template.stages.map((stage, index) => (
                <button
                  key={stage}
                  type="button"
                  className={`create-sub-project-form__stage-btn ${selectedStage === stage ? 'create-sub-project-form__stage-btn--selected' : ''}`}
                  onClick={() => setSelectedStage(stage)}
                >
                  <span className="create-sub-project-form__stage-number">{index + 1}</span>
                  <span className="create-sub-project-form__stage-name">{stage}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="create-sub-project-form__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? t('common.loading') : t('works.subProjects.createSubProject')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateSubProjectModal;
